import React, { useState, useCallback, useEffect } from 'react';
import { TimelineEditor } from './studio/TimelineEditor';
import { PreviewMonitor } from './studio/PreviewMonitor';
import { AssetPanel } from './studio/AssetPanel';
import { InspectorPanel } from './studio/InspectorPanel';
import { ArrowLeftIcon } from './icons/ArrowLeftIcon';
import { v4 as uuidv4 } from 'uuid';

export const Studio = ({ projectData, onProjectUpdate, onBack }) => {
    const [playheadPosition, setPlayheadPosition] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [selectedClipId, setSelectedClipId] = useState(null);
    const [isAssetPanelCollapsed, setIsAssetPanelCollapsed] = useState(false);
    const [isInspectorCollapsed, setIsInspectorCollapsed] = useState(false);
    const [activeTool, setActiveTool] = useState('select');

    useEffect(() => {
        if (!projectData.timeline) {
            const newTimeline = {
                tracks: [
                    { id: 'track-video', type: 'video', name: 'Video' },
                    { id: 'track-audio-music', type: 'audio', name: 'Music' },
                    { id: 'track-audio-sfx', type: 'audio', name: 'SFX' },
                    { id: 'track-text', type: 'text', name: 'Text' }
                ],
                clips: [],
                duration: 10
            };
            onProjectUpdate({ ...projectData, timeline: newTimeline });
        }
    }, []);

    const recalculateDuration = (clips) => {
        const maxEnd = clips.reduce((max, c) => Math.max(max, c.start + c.duration), 0);
        return Math.max(10, maxEnd + 1);
    };

    const handleClipSelected = useCallback((clip) => {
        setSelectedClipId(clip ? clip.id : null);
    }, []);

    const handleUpdateClip = useCallback((updatedClip) => {
        if (!projectData.timeline) return;
        
        const newClips = projectData.timeline.clips.map(c => 
            c.id === updatedClip.id ? updatedClip : c
        );
        
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: {
                ...projectData.timeline,
                clips: newClips,
                duration: newDuration
            }
        });
    }, [projectData, onProjectUpdate]);

    const handleDeleteClip = useCallback((clipId) => {
        if (!projectData.timeline) return;
        const newClips = projectData.timeline.clips.filter(c => c.id !== clipId);
        
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
        if (selectedClipId === clipId) setSelectedClipId(null);
    }, [projectData, onProjectUpdate, selectedClipId]);

    const handleAddAsset = useCallback((asset) => {
        onProjectUpdate({
            ...projectData,
            assets: [...projectData.assets, asset]
        });
    }, [projectData, onProjectUpdate]);

    const handleAddTextClip = useCallback(() => {
        if (!projectData.timeline) return;
        const textTrack = projectData.timeline.tracks.find(t => t.type === 'text');
        if (!textTrack) return;

        const newClip = {
            id: `clip-text-${uuidv4()}`,
            type: 'text',
            trackId: textTrack.id,
            start: playheadPosition,
            duration: 5,
            assetId: 'text-asset',
            content: 'New Text',
            fontSize: 48,
            color: '#ffffff',
            fontFamily: 'sans-serif'
        };

        const newClips = [...projectData.timeline.clips, newClip];
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
        setSelectedClipId(newClip.id);
    }, [projectData, playheadPosition, onProjectUpdate]);

    const handleAddAudioClip = useCallback((asset, trackName) => {
        if (!projectData.timeline) return;
        const audioTrack = projectData.timeline.tracks.find(t => t.name === trackName);
        if (!audioTrack) return;

        const newClip = {
            id: `clip-audio-${uuidv4()}`,
            type: 'audio',
            trackId: audioTrack.id,
            start: playheadPosition,
            duration: 10,
            assetId: asset.id,
            name: asset.name,
            volume: 1
        };

        const newClips = [...projectData.timeline.clips, newClip];
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
        setSelectedClipId(newClip.id);
    }, [projectData, playheadPosition, onProjectUpdate]);

    const handleSplitClip = useCallback((clipId, splitTime) => {
        if (!projectData.timeline) return;
        const clip = projectData.timeline.clips.find(c => c.id === clipId);
        if (!clip) return;

        const relativeSplit = splitTime - clip.start;
        
        if (relativeSplit <= 0.1 || relativeSplit >= clip.duration - 0.1) return;

        const firstPartDuration = relativeSplit;
        const secondPartDuration = clip.duration - relativeSplit;

        const firstClip = { ...clip, duration: firstPartDuration };
        const secondClip = { 
            ...clip, 
            id: `clip-${uuidv4()}`, 
            start: clip.start + firstPartDuration, 
            duration: secondPartDuration 
        };

        const newClips = projectData.timeline.clips
            .filter(c => c.id !== clipId)
            .concat([firstClip, secondClip]);

        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
    }, [projectData, onProjectUpdate]);

    const selectedClip = projectData.timeline?.clips.find(c => c.id === selectedClipId) || null;

    if (!projectData.timeline) return <div className="text-white p-8">Initializing Studio...</div>;

    return (
        <div className="flex flex-col h-full bg-[#101018] text-white overflow-hidden">
            <header className="h-14 border-b border-gray-700 flex items-center px-4 bg-[#181820] shrink-0 justify-between">
                <div className="flex items-center">
                    <button 
                        onClick={onBack}
                        className="flex items-center text-gray-400 hover:text-white transition-colors mr-4"
                    >
                        <ArrowLeftIcon className="w-5 h-5 mr-2" />
                        Back to Storyboard
                    </button>
                    <h2 className="text-lg font-bold">Studio Editor</h2>
                </div>
                <div className="text-sm text-gray-400">
                    {projectData.name}
                </div>
            </header>

            <div className="flex-grow flex overflow-hidden">
                <AssetPanel 
                    project={projectData} 
                    onAddTextClip={handleAddTextClip}
                    onAddAudioClip={handleAddAudioClip}
                    onAddAsset={handleAddAsset}
                    isCollapsed={isAssetPanelCollapsed}
                    setIsCollapsed={setIsAssetPanelCollapsed}
                />
                
                <div className="flex-grow flex flex-col min-w-0">
                    <div className="flex-grow flex flex-col min-h-0 relative">
                        <PreviewMonitor 
                            project={projectData} 
                            playheadPosition={playheadPosition}
                            setPlayheadPosition={setPlayheadPosition}
                            isPlaying={isPlaying}
                            setIsPlaying={setIsPlaying}
                            onProjectUpdate={onProjectUpdate}
                        />
                    </div>
                    <div className="h-1/3 border-t border-gray-700 min-h-[200px]">
                        <TimelineEditor 
                            project={projectData}
                            playheadPosition={playheadPosition}
                            setPlayheadPosition={setPlayheadPosition}
                            onClipSelected={handleClipSelected}
                            selectedClipId={selectedClipId}
                            onUpdateClipInProgress={handleUpdateClip}
                            onUpdateClipComplete={handleUpdateClip}
                            onDeleteClip={handleDeleteClip}
                            isPlaying={isPlaying}
                            activeTool={activeTool}
                            setActiveTool={setActiveTool}
                            onSplitClip={handleSplitClip}
                        />
                    </div>
                </div>

                <InspectorPanel 
                    selectedClip={selectedClip}
                    project={projectData}
                    onUpdateClip={handleUpdateClip}
                    onDeleteClip={handleDeleteClip}
                    isCollapsed={isInspectorCollapsed}
                    setIsCollapsed={setIsInspectorCollapsed}
                />
            </div>
        </div>
    );
};
